import 'datatables.net-bs5';
import DataTable from 'datatables.net-buttons-bs5';
import 'datatables.net-buttons/js/buttons.print.min';
import 'datatables.net-buttons/js/buttons.html5.min';

import jszip from 'jszip';
import pdfMake from 'pdfmake/build/pdfmake.min';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { DateTime } from 'luxon';

import addDefaultButtonConfig from './table-defaultConfig-buttons';
import addDefaultConfig from './table-defaultConfig';

DataTable.Buttons.jszip(jszip);
DataTable.Buttons.pdfMake(pdfMake);

pdfMake.vfs = pdfFonts.pdfMake.vfs;

addDefaultConfig(DataTable);
addDefaultButtonConfig(DataTable);

const handleKeyboardEvent = (event) => {
    // enter key is a click except for links
    if ((event.key === 'Enter') && (event.target.localName !== 'a')) {
        handleClickEvent(event);
    }
};

function handleClickEvent(event) {
    let node = event.target;

    if (['A', 'TH'].includes(node.nodeName)) return;

    // find the row
    if (node.nodeName !== 'TR') {
        while ((node = node.parentNode)) {
            if (node.nodeName === 'TR') break;
        }
    }

    if (!node) {
        return;
    }

    const allowDetail = node.getAttribute('data-allowdetail');
    let url;

    if (allowDetail && !allowDetail.match(/false/i)) {
        const dataUrl = node.getAttribute('data-url');
        if (dataUrl) {
            url = dataUrl;
        } else {
            url = `${node.parentNode.parentNode.getAttribute('data-url')}${node.id}/edit`;
        }
    }

    if (url) {
        url = util.urlBase() + url;
        if (!util.openInNewWindowIf(event, url)) window.location = url;
    }
}

const selectAllCheckboxRows = (table) => (event) => {
    const checkboxes = Array.from(table.getElementsByClassName('select-row-checkbox'));
    const { checked } = event.target;
    const checkboxEvent = new CustomEvent('table-select-all-click', {
        detail: {
            target: event.target,
        },
    });
    checkboxes.forEach((x) => {
        x.checked = checked;
    });
    table.dispatchEvent(checkboxEvent);
};

const bindRowCheckboxHandlers = (table) => {
    const selectAll = table.querySelector('.select-all-rows');
    const checkboxes = Array.from(table.getElementsByClassName('select-row-checkbox'));

    const checkboxEvent = new CustomEvent('table-checkbox-click', {
        detail: {
            table,
        },
    });

    table.addEventListener('click', (e) => {
        if (e.target
            && e.target.type === 'checkbox'
            && e.target.classList.contains('select-row-checkbox')) {
            table.dispatchEvent(checkboxEvent);
            selectAll.checked = checkboxes.every((c) => c.checked) && e.target.checked;
        }
    });
};

const bindCheckboxHandlers = (table) => {
    const selectAll = table.querySelector('.select-all-rows');
    if (selectAll) {
        selectAll.addEventListener('click', selectAllCheckboxRows(table));
        bindRowCheckboxHandlers(table);
    }
};

const addCheckboxes = (jsonVar) => {
    const i = jsonVar.columns.findIndex((x) => x.name === 'aurion_id');
    const vi = jsonVar.columns.findIndex((x) => x.name === 'aurion_version');
    const ti = jsonVar.columns.findIndex((x) => x.name === 'aurion_table_name');
    const sci = jsonVar.columns.findIndex((x) => x.name === 'aurion_show_checkbox');

    jsonVar.columns.unshift({
        name: 'select-row',
        title: '<input type="checkbox" name="select-all-rows" class="select-all-rows" aria-label="Select all rows">',
        orderable: false,
        targets: 0,
        width: '1%',
    });

    jsonVar.data = jsonVar.data.map((x) => {
        let d = '';
        // show checkbox when no show checkbox specified or it is specified and true
        if (sci === -1 || x[sci]) {
            d = `<input name="select-row-checkbox" class="select-row-checkbox" type="checkbox" value="${x[i]}" data-version="${x[vi]}" data-table="${x[ti]}" aria-label="Select row">`;
        }
        x.unshift(d);
        return x;
    });

    return jsonVar;
};

// since some tables use raw data the tab index must be added here to each field for keyboard accessibility
const setTableFieldTabIndex = (target) => {
    [...target.getElementsByTagName('tr')].forEach((tr) => {
        tr.setAttribute('tabindex', 0);
    });
};

function init() {
    const datatables = document.querySelectorAll('table.table-datatables');
    datatables.forEach((table) => {
        // Guard to protect from re-instantiating datatable.
        if (isDataTable(table)) return;

        const hasCheckboxes = table.matches('[data-has-checkboxes]');
        let datatablesOpts = {
            buttons: [],
            deferRender:  true,
            info: false,
            autoWidth: false,
            language: {
                buttons: {
                    pageLength: {
                        _: 'Show %d entries',
                    },
                },
                search: 'Filter:',
                processing: 'Processing...',
            },
            order: [],
            ordering: false,
            paging: false,
            searching: false,
        };

        if (hasCheckboxes) {
            datatablesOpts = $.extend({}, datatablesOpts, {
                drawCallback: () => {
                    $('.select-row-checkbox, .select-all-rows', table).prop('checked', false);
                },
            });
        }

        // if there is a json variable configured then consume the data from there instead of
        // parsing the HTML structure of the table
        if (table.matches('[data-json-variable]') && table.getAttribute('data-json-variable')) {
            const jsonEl = document.getElementById(table.getAttribute('data-json-variable'));
            let jsonVar = JSON.parse(jsonEl.textContent || jsonEl.innerText);

            const rowValues = {
                hasDetailView: table.matches('[data-has-detail-view]'),
            };
            // find the interesting hidden columns key
            jsonVar.columns.forEach((v, k) => {
                if (v.name === 'aurion_id') {
                    rowValues.id = k;
                    // hack to fix the checkbox offsetting the row id
                    if (hasCheckboxes) {
                        rowValues.id = k + 1;
                    }
                } else if (v.name === 'aurion_url') {
                    rowValues.url = k;
                }
            });

            if (hasCheckboxes) {
                jsonVar = addCheckboxes(jsonVar);
            }

            datatablesOpts = $.extend({}, jsonVar, {
                autoWidth: false,
                columnDefs: [
                    {
                        targets: '_all',
                        createdCell: (cell, cellData, rowData, rowIndex, colIndex) => {
                            $(cell).attr('data-th', jsonVar.columns[colIndex].title);
                        },
                    },
                ],
                initComplete: (settings) => {
                    $(settings.nTHead).addClass('table-dark');
                    if (hasCheckboxes) {
                        bindCheckboxHandlers(table);
                    }
                },
                rowCallback: (row, data) => {
                    const $row = $(row);
                    if ('id' in rowValues && data[rowValues.id]) {
                        $row.attr('id', data[rowValues.id]);
                    }
                    if ('url' in rowValues && data[rowValues.url]) {
                        $row.attr('data-url', data[rowValues.url]);
                    }
                    if (rowValues.hasDetailView) {
                        $row.attr('data-allowDetail', 'true');
                        $row.addClass('table-clickable-row');
                    }
                },
            }, datatablesOpts);
        }

        if (table.hasAttribute('data-server-side')) {
            // all but the initial draw should use AJAX requests
            datatablesOpts.columnDefs = [
                {
                    createdCell(cell, cellData, rowData, rowIndex, colIndex) {
                        if (rowData.columns) {
                            // this is AJAX data so we need to mimic a normal cell
                            const column = rowData.columns[colIndex];
                            cell.classList.add(column['data-type']);
                            cell.setAttribute('data-th', column['data-th']);

                            if (column['data-type'] === 'date' && column.value) {
                                // we need to provide a DataTables-friendly date for ordering
                                cell.setAttribute(
                                    'data-order',
                                    DateTime.fromFormat(column.value, 'dd/MM/yyyy').toFormat('yyyy-MM-dd'),
                                );
                            }
                        }
                    },
                    targets: '_all',
                },
            ];
        }

        if (table.hasAttribute('data-status')) {
            const status = table.getAttribute('data-status');
            const pendingUrl = new URL(window.location.href);
            pendingUrl.searchParams.set('status', 'pending');

            const allUrl = new URL(window.location.href);
            allUrl.searchParams.set('status', 'all');

            datatablesOpts.buttons.push([`
                <div class="btn-group btn-group-sm btn-toggles" role="group">
                    <a href="${pendingUrl.toString()}" class="btn btn-sm buttons-collection buttons-page-length ${status === 'PENDING' ? 'active' : ''}">Pending</a>
                    <a href="${allUrl.toString()}" class="btn btn-sm buttons-collection buttons-page-length ${status === 'ALL' ? 'active' : ''}">All</a>
                </div>
            `]);

            datatablesOpts.language.emptyTable = '<span class="w-100 d-flex justify-content-center table-no-results pt-3">No forms found.</span>';
        }

        if (table.hasAttribute('data-paging')) {
            datatablesOpts.buttons.push({
                extend: 'pageLength',
                fade: 0,
            });
        }

        if (table.hasAttribute('data-download-all') && util.getDeviceType() !== 'mobile') {
            const downloadType = table.getAttribute('data-download-all');
            let apiUrl;
            let fileBaseUrl;

            if (downloadType === 'past-employee-pay-summaries') {
                apiUrl = `${util.urlBase()}past-employees/api/download-all-pay-summaries`;
                fileBaseUrl = `${util.urlBase()}past-employees/file/39/`;
            }

            if (apiUrl) {
                datatablesOpts.buttons.push({
                    text: 'Download All',
                    action(e) {
                        if (e) e.preventDefault();

                        window.loading.load();

                        ajax({
                            url:          apiUrl,
                            method:       'POST',
                            documentType: 'json',
                            success(response) {
                                window.location = `${fileBaseUrl}${response.fileUrl}`;
                                return { notify: { title: 'Success' } };
                            },
                            callback() {
                                window.loading.remove();
                            },
                        });
                    },
                });
            }
        }

        if (table.hasAttribute('data-full-screen') && util.getDeviceType !== 'mobile') {
            const fullScreenClass = 'dataTables_full-screen';
            datatablesOpts.buttons.push({
                text: 'Full Screen',
                action(e) {
                    const btn = e.currentTarget;
                    const btnText = btn.getElementsByTagName('span')[0];
                    let section = btn.parentNode;
                    while (section && section.nodeName !== 'SECTION') {
                        section = section.parentNode;
                    }
                    if (!section) return;

                    if (section.classList.contains(fullScreenClass)) {
                        section.classList.remove(fullScreenClass);
                        section.style.height = '';
                        btnText.textContent = 'Full Screen';
                        const iconEl = btn.getElementsByTagName('i')[0];
                        if (iconEl) btn.removeChild(iconEl);
                        section.scrollIntoView();
                    } else {
                        section.classList.add(fullScreenClass);
                        section.style.height = `${document.body.scrollHeight}px`;
                        btnText.textContent = ' Done';
                        const iconEl = document.createElement('i');
                        iconEl.classList.add('fa-light');
                        iconEl.classList.add('circle-chevron-left');
                        btn.insertAdjacentElement('afterbegin', iconEl);
                        window.scrollTo(0, 0);
                    }
                },
            });
        }

        if (table.hasAttribute('data-export')) {
            const exportOptions = {
                columns: ':visible:not(.rowselector)',
                format: {
                    body(data) {
                        return data
                            .replace(/<br>/g, '\n')
                            .replace(/&amp;/g, '&')
                            .replace(/<[^>]*>/g, '');
                    },
                },
            };
            const getHeading = (element) => {
                if (/H[1-6]/.test(element.tagName)) return element.textContent.trim();

                if (element.tagName === 'HEADER' || element.className === 'header') {
                    const heading = Array.from(element.children)
                        .find((child) => /H[1-6]/.test(child.tagName));
                    if (heading) return heading.textContent.trim();
                }
            };
            const findHeading = () => {
                let parent = table;

                while ((parent = parent.parentElement)) {
                    let heading = getHeading(parent);
                    if (heading) return heading;

                    let sibling = parent;

                    while ((sibling = sibling.previousElementSibling)) {
                        heading = getHeading(sibling);
                        if (heading) return heading;
                    }
                }

                return document.title;
            };

            datatablesOpts.buttons.push({
                autoClose: true,
                buttons: [
                    {
                        exportOptions,
                        extend: 'copy',
                        title: findHeading,
                        action: (e, dt, button, config, cb) => {
                            notification.notify('Success', 'Copied to Clipboard.');
                            // Get the default copyHtml5 function and call it manually
                            DataTable.ext.buttons.copyHtml5.action.call(
                                this,
                                e,
                                dt,
                                button,
                                config,
                                cb,
                            );
                        },
                    },
                    {
                        exportOptions,
                        extend: 'csv',
                        filename: findHeading,
                        action: (e, dt, button, config, cb) => {
                            notification.notify('Success', 'Your file is ready! Check your downloads folder.');
                            // Get the default csv Html5 function and call it manually
                            DataTable.ext.buttons.csvHtml5.action.call(
                                this,
                                e,
                                dt,
                                button,
                                config,
                                cb,
                            );
                        },
                    },
                    {
                        exportOptions,
                        extend: 'excel',
                        filename: findHeading,
                        title: findHeading,
                        action: (e, dt, button, config, cb) => {
                            notification.notify('Success', 'Your file is ready! Check your downloads folder.');
                            // Get the default csv Html5 function and call it manually
                            DataTable.ext.buttons.excelHtml5.action.call(
                                this,
                                e,
                                dt,
                                button,
                                config,
                                cb,
                            );
                        },
                    },
                    {
                        exportOptions,
                        extend: 'pdf',
                        filename: findHeading,
                        title: findHeading,
                        action: (e, dt, button, config, cb) => {
                            notification.notify('Success', 'Your file is ready! Check your downloads folder.');
                            // Get the default csv Html5 function and call it manually
                            DataTable.ext.buttons.pdfHtml5.action.call(
                                this,
                                e,
                                dt,
                                button,
                                config,
                                cb,
                            );
                        },
                    },
                    {
                        exportOptions,
                        extend: 'print',
                        title: findHeading,
                    },
                ],
                extend: 'collection',
                fade: 0,
                text: 'Export',
            });
        }

        // eslint-disable-next-line no-new
        new DataTable(table, datatablesOpts);
        table.addEventListener('draw', finalise);
    });

    Array.from(document.getElementsByClassName('table')).forEach((node) => {
        if (!node.hasAttribute('data-raw-list')) {
            node.addEventListener('click', handleClickEvent);
            node.addEventListener('keyup', handleKeyboardEvent);
        }
    });

    finalise();
    initialiseClickableUrlCells();
}

function finalise(event) {
    const target = event ? window.util.parent(event.target, 'div.dataTables_wrapper') : document;
    target.querySelectorAll('.dt-button-down-arrow')?.forEach((pageSel) => pageSel.classList.add('d-none'));
    Array.from(target.querySelectorAll('input.form-control.form-control-xs')).forEach((filter) => {
        filter.name = 'datatables-filter';
    });
    setTableFieldTabIndex(target);
}

function initialiseClickableUrlCells() {
    Array.from(document.querySelectorAll('.table-clickable-url-cell')).forEach((node) => {
        if (node.hasAttribute('data-href')) {
            node.addEventListener('click', (event) => {
                const { target } = event;
                if (target.hasAttribute('data-href')) {
                    window.document.location = target.getAttribute('data-href');
                }
            });
        }
    });
}

/**
 * Checks if the element is a DataTable
 * @param {HTMLElement} element
 * @returns Boolean
 */
function isDataTable(element) {
    return element.classList.contains('dataTable') || DataTable.isDataTable(element);
}

/**
 * Takes an element and returns it with DataTables API attached
 * @param {HTMLElement} element
 * @returns DataTable
 */
function getDataTable(element) {
    return DataTable.Api(element);
}

document.addEventListener('DOMContentLoaded', init);

// Need to do this for ERM Send Messages
// Or make it so init doesn't run on page load - this is easier.
window.DataTable = DataTable;

export default {
    init,
    isDataTable,
    getDataTable,
};
